/**
 * PWA bootstrap — service worker registration + web-push subscription.
 *
 * - registerPwa(): registers /sw.js (run once at app boot).
 * - requestNotificationPermission(): must be called from a user gesture
 *   (click handler), per browser policy. On iOS the PWA must be installed
 *   via "Add to Home Screen" first.
 * - subscribeToPush(): turns the SW push manager into a server-side
 *   PushSubscription row via POST /api/chat/push/subscribe.
 */

import { logger } from './logger';

const VAPID_PUBLIC_KEY = (import.meta.env.VITE_VAPID_PUBLIC_KEY as string | undefined) ?? '';

export async function registerPwa(): Promise<ServiceWorkerRegistration | null> {
  if (typeof window === 'undefined' || !('serviceWorker' in navigator)) return null;
  try {
    const reg = await navigator.serviceWorker.register('/sw.js', { scope: '/' });
    return reg;
  } catch (err) {
    logger.debug('[pwa] service worker registration failed', err);
    return null;
  }
}

export function isStandalone(): boolean {
  if (typeof window === 'undefined') return false;
  const matchMedia = window.matchMedia && window.matchMedia('(display-mode: standalone)').matches;
  const iosStandalone = (window.navigator as Navigator & { standalone?: boolean }).standalone === true;
  return Boolean(matchMedia || iosStandalone);
}

export async function notificationPermissionState(): Promise<NotificationPermission> {
  if (typeof Notification === 'undefined') return 'denied';
  return Notification.permission;
}

export async function requestNotificationPermission(): Promise<NotificationPermission> {
  if (typeof Notification === 'undefined') return 'denied';
  if (Notification.permission === 'granted' || Notification.permission === 'denied') {
    return Notification.permission;
  }
  return Notification.requestPermission();
}

export async function subscribeToPush(
  authToken: string,
  workspaceId: string,
): Promise<PushSubscription | null> {
  if (!VAPID_PUBLIC_KEY) {
    logger.debug('[pwa] VITE_VAPID_PUBLIC_KEY not configured — push disabled');
    return null;
  }
  if (typeof navigator === 'undefined' || !('serviceWorker' in navigator) || !('PushManager' in window)) {
    return null;
  }

  const reg = (await navigator.serviceWorker.ready) ?? null;
  if (!reg) return null;

  const existing = await reg.pushManager.getSubscription();
  const sub =
    existing ??
    (await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY),
    }));

  try {
    const subJson = sub.toJSON() as { endpoint?: string; keys?: { p256dh?: string; auth?: string } };
    if (!subJson.endpoint || !subJson.keys?.p256dh || !subJson.keys?.auth) return sub;

    const apiBase = (import.meta.env.VITE_API_URL as string | undefined) ?? '';
    await fetch(`${apiBase}/api/chat/push/subscribe`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${authToken}`,
        'x-workspace-id': workspaceId,
      },
      body: JSON.stringify({
        endpoint: subJson.endpoint,
        keys: { p256dh: subJson.keys.p256dh, auth: subJson.keys.auth },
        userAgent: navigator.userAgent,
      }),
    });
  } catch (err) {
    logger.debug('[pwa] subscribe POST failed', err);
  }
  return sub;
}

export async function unsubscribeFromPush(authToken: string, workspaceId: string): Promise<void> {
  if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return;
  const reg = (await navigator.serviceWorker.ready) ?? null;
  if (!reg) return;
  const sub = await reg.pushManager.getSubscription();
  if (!sub) return;
  const endpoint = sub.endpoint;
  await sub.unsubscribe().catch(() => undefined);
  try {
    const apiBase = (import.meta.env.VITE_API_URL as string | undefined) ?? '';
    await fetch(`${apiBase}/api/chat/push/unsubscribe`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${authToken}`,
        'x-workspace-id': workspaceId,
      },
      body: JSON.stringify({ endpoint }),
    });
  } catch (err) {
    logger.debug('[pwa] unsubscribe POST failed', err);
  }
}

// VAPID public key in URL-safe base64 → ArrayBuffer (web-push spec).
// PushManager.subscribe() expects BufferSource; using an ArrayBuffer avoids
// the SharedArrayBuffer-tinted Uint8Array typing under stricter lib.dom.
function urlBase64ToUint8Array(base64String: string): ArrayBuffer {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = window.atob(base64);
  const buf = new ArrayBuffer(raw.length);
  const view = new Uint8Array(buf);
  for (let i = 0; i < raw.length; i += 1) view[i] = raw.charCodeAt(i);
  return buf;
}
